import type { SimulationState, ThemeData } from '../types';
import type { GameEvent } from '../events/GameEvents';
import type { SeededRandom } from '../rng/SeededRandom';

export function calculateFinalScore(state: SimulationState): {
  score: number;
  breakdown: Record<string, number>;
  rank: string;
} {
  const productsScore = state.company.totalProductsReleased * 100;
  const hofScore = state.company.hallOfFameCount * 500;
  const fanScore = Math.min(state.company.fans, 10000);
  const revenueScore = state.company.totalRevenue > 0
    ? Math.floor(Math.log10(state.company.totalRevenue)) * 1000
    : 0;
  const levelScore = state.company.level * 200;
  const avgStaffLevel =
    state.staff.length > 0
      ? state.staff.reduce((sum, s) => sum + s.level, 0) / state.staff.length
      : 1;
  const staffScore = Math.round(avgStaffLevel * 50);

  // Loyalty bonus: staff who were never on notice / never left
  // Approximated by counting staff with high yearsEmployed
  const loyalStaff = state.staff.filter((s) => !s.isCEO && s.yearsEmployed >= 5).length;
  const loyaltyScore = loyalStaff * 150;

  const score = productsScore + hofScore + fanScore + revenueScore + levelScore + staffScore + loyaltyScore;

  let rank: string;
  if (score >= 50000) rank = 'S';
  else if (score >= 30000) rank = 'A';
  else if (score >= 15000) rank = 'B';
  else if (score >= 5000) rank = 'C';
  else rank = 'D';

  return {
    score,
    breakdown: {
      products: productsScore,
      hallOfFame: hofScore,
      fans: fanScore,
      revenue: revenueScore,
      companyLevel: levelScore,
      staffLevel: staffScore,
      loyalty: loyaltyScore,
    },
    rank,
  };
}

export function prestigeSystemUpdate(
  state: SimulationState,
  themeData: ThemeData,
  _rng: SeededRandom,
): GameEvent[] {
  const events: GameEvent[] = [];

  // Check for campaign completion
  if (
    state.company.currentYear > themeData.balance.campaignYears &&
    !state.tutorialTipsShown.includes('campaign_completed')
  ) {
    state.tutorialTipsShown.push('campaign_completed');
    const { score, rank } = calculateFinalScore(state);

    events.push({
      type: 'CAMPAIGN_COMPLETED',
      year: state.company.currentYear,
      finalScore: score,
      rank,
    });
  }

  // Company level-up check
  while (state.company.xp >= state.company.xpToNextLevel) {
    state.company.xp -= state.company.xpToNextLevel;
    state.company.level++;
    state.company.xpToNextLevel = Math.round(state.company.xpToNextLevel * 1.5);

    events.push({
      type: 'COMPANY_LEVELED_UP',
      newLevel: state.company.level,
    });
  }

  return events;
}
